import Button from "@Components/Button";
import Tab from "@Components/Tabs/Tab";
import Tabs from "@Components/Tabs/Tabs";
import TabsList from "@Components/Tabs/TabsList";
import Panel from "@Pages/Search/components/NearbySection/components/Panel";
import styles from "./filterSubsection.module.scss";
import Budget from "./components/Budget";
import RoomType from "./components/RoomType";
import MoveInMonth from "./components/MoveInMonth";
import { AppState } from "@Store/index";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import filtersModalReducer from "@Pages/Search/components/FilterSection/components/FiltersModal/filtersModalReducer";
import { useReducer, useState } from "react";
import { applyFilter } from "@Pages/Search/data/searchPageReducer";
import { SEARCH_SUBSECTION_FILTERS } from "@Pages/Search/components/FilterSection/data/filtersList";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getFilterActiveStatus, { FilterNameType } from "@Pages/Search/utils/getFilterActiveStatus";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import fetchMoveInMonths from "./utils";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, FILTER_FLOW, FILTER_MODE } from "@Utils/webApputils/analyticEvents";

const FilterSubsection = ({ isDesktop = false }) => {
  const searchListDispatch = useDispatch();

  const { dynamicFilterCountData, filters, region, isRadiusExpanded, radiusExpandEnabled } =
    useSelector(
      ({ searchPageData }: AppState) => ({
        dynamicFilterCountData: searchPageData?.dynamicFilterCountData,
        filters: searchPageData?.filters,
        region: searchPageData.region,
        isRadiusExpanded: searchPageData.isRadiusExpanded,
        radiusExpandEnabled: searchPageData.radiusExpandEnabled,
      }),
      shallowEqual,
    );
  const { isWhitelabel, partner_id, isUniversityPartner, isRestrictedSearchEnabled } =
    useWhitelabel();

  const [currFilters, dispatch] = useReducer(filtersModalReducer, filters);

  const [forceReset, setForceReset] = useState(0);

  const filtersActiveStatus = getFilterActiveStatus("Filters" as FilterNameType, currFilters);

  const handleApplyFilters = () => {
    // shallow copy and remove sortKey
    const filtersToApply = { ...currFilters };
    delete filtersToApply.sortKey;

    logAppEvent({
      eventName: APP_EVENTS.FILTER_APPLIED,
      payload: {
        filter_flow: FILTER_FLOW.INLINE_FILTER,
        mode: FILTER_MODE.LISTING_VIEW,
        screenName: APP_PAGE.SEARCH,
        filterType: Object.keys(filtersToApply).join(","),
        filterValue: filtersToApply,
      },
    });
    gtagEventEmitter({
      action: "SP_filter_subsection_filter_apply_click",
      category: "SP_filter_subsection",
      value: true,
    });
    searchListDispatch(applyFilter(currFilters));
  };

  const handleResetFilters = () => {
    dispatch({
      type: "applyAll",
      payload: {},
    });
    setForceReset((v) => v + 1);
  };

  const handleFilterSelect = (filterName: string) => {
    if (
      filterName === t("searchpage.searchlist.filtersubsection.moveinmonth") &&
      !dynamicFilterCountData?.data?.data?.move_in_month
    ) {
      fetchMoveInMonths({
        currFilters,
        filters,
        region,
        searchListDispatch,
        dynamicFilterCountData,
        isWhitelabel,
        partner_id,
        isUniversityPartner,
        isRestrictedSearchEnabled,
        isRadiusExpanded,
        radiusExpandEnabled,
      });
    }
    logAppEvent({
      eventName: APP_EVENTS.FILTER_CTA_CLICKED,
      payload: {
        filter_name: filterName,
        filter_flow: FILTER_FLOW.INLINE_FILTER,
        screen_name: APP_PAGE.SEARCH,
      },
    });
    gtagEventEmitter({
      action: "SP_filter_subsection_filter_click",
      category: "SP_filter_subsection",
      value: filterName,
    });
  };

  const { t } = useTranslationHook();

  return (
    <div
      data-testid="Inline-Filter-V1"
      className={formatClassNames(styles.subsectionContainer, isDesktop && styles.desktop)}
    >
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>
            {t("searchpage.searchlist.filtersubsection.filters")}&nbsp;
          </span>
          <span className={styles.title_highlight}>
            {t("searchpage.searchlist.filtersubsection.foryou")}
          </span>
        </div>
        <button
          type="button"
          className={formatClassNames(styles.reset, !filtersActiveStatus && styles.disabled)}
          onClick={handleResetFilters}
          disabled={!filtersActiveStatus}
        >
          {t("searchpage.searchlist.filtersubsection.reset")}
        </button>
      </div>

      {/* Filter Tabs */}
      <Tabs
        defaultValue="Budget"
        className={formatClassNames(styles.tabs, isDesktop && styles.desktop)}
      >
        <TabsList>
          {SEARCH_SUBSECTION_FILTERS.map(({ name, key, translationKey }) => (
            <Tab value={name} key={key} onClick={() => handleFilterSelect(name)}>
              {t(translationKey)}
            </Tab>
          ))}
        </TabsList>

        <Panel name="Budget">
          <Budget key={forceReset} value={currFilters?.budget} dispatch={dispatch} />
        </Panel>
        <Panel name="Room Type">
          <RoomType value={currFilters?.roomType as string[]} dispatch={dispatch} isInlineFilters />
        </Panel>
        <Panel name="Move in Month">
          <MoveInMonth
            value={currFilters?.moveInMonth as string}
            dynamiCount={dynamicFilterCountData?.data?.data?.move_in_month}
            dispatch={dispatch}
            isInlineFilters
          />
        </Panel>
      </Tabs>
      <div className={formatClassNames(styles.btn, isDesktop && styles.desktop)}>
        <Button
          variant="primary"
          className={formatClassNames(styles.applyFilterBtn, isDesktop && styles.desktop)}
          onClick={handleApplyFilters}
          greyDisabled={!filtersActiveStatus}
        >
          {t("searchpage.searchlist.filtersubsection.applyfilter")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(FilterSubsection, {
  componentName: "FilterSubsection-SearchListDesktop-SearchPage",
  showForChina: true,
  showForWhiteLable: false,
});
